import React from 'react';
import { ArrowLeft, ArrowRight, CheckCircle2, AlertCircle, HelpCircle, Phone } from 'lucide-react';
import { servicesData } from '../data/servicesData';
import { DigitalMarketingPlans } from '../components/home/DigitalMarketingPlans';
import { ServiceItem } from '../types';

interface ServiceDetailPageProps {
  slug: string;
  onNavigate: (path: string) => void;
  onOpenConsultation: (serviceName?: string) => void;
}

export const ServiceDetailPage: React.FC<ServiceDetailPageProps> = ({ slug, onNavigate, onOpenConsultation }) => {
  const service = servicesData.find(s => s.slug === slug) || servicesData[0];

  const related = servicesData.filter(s => service.relatedServices.includes(s.slug));

  return (
    <div className="bg-[#071426] text-[#D5DFEC] py-10 space-y-16">
      
      {/* Breadcrumb & Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => onNavigate('/services')}
          className="inline-flex items-center gap-2 text-xs font-semibold text-[#8FA0B8] hover:text-white transition-colors mb-6"
        >
          <ArrowLeft size={14} />
          <span>Back to All Services</span>
        </button>

        <div className="max-w-4xl space-y-5">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0B2342] border border-[#1b2f4a] text-xs font-semibold uppercase tracking-wider text-[#FF851B]">
            <span>{service.accentColor.toUpperCase()} ARCHITECTURE</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white font-display tracking-tight leading-tight">
            {service.title}
          </h1>

          <p className="text-base sm:text-xl text-[#D5DFEC] leading-relaxed font-normal">
            {service.fullDescription}
          </p>

          <div className="flex flex-wrap gap-4 pt-3">
            <button
              onClick={() => onOpenConsultation(service.title)}
              className="px-7 py-3.5 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white font-bold text-sm shadow-lg hover:shadow-glow-orange transition-all flex items-center gap-2"
            >
              <Phone size={16} />
              <span>Book a Consultation for this Service</span>
            </button>
          </div>
        </div>
      </section>

      {/* Challenges & Solutions Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          
          {/* Challenges */}
          <div className="p-8 rounded-2xl bg-[#050A13] border border-[#1b2f4a] space-y-4">
            <div className="flex items-center gap-2.5 text-red-400 font-bold text-sm uppercase tracking-wider">
              <AlertCircle size={18} />
              <span>Industry Challenges Addressed</span>
            </div>
            <h2 className="text-xl font-bold text-white font-display">Common Pain Points We Resolve</h2>
            <div className="space-y-3 pt-2">
              {service.challenges.map((c, i) => (
                <div key={i} className="flex items-start gap-3 text-xs sm:text-sm text-[#D5DFEC] p-3 rounded-lg bg-[#071426] border border-[#1b2f4a]/60">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 shrink-0" />
                  <span>{c}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Solutions & Benefits */}
          <div className="p-8 rounded-2xl bg-[#0c1a2d] border border-[#1b2f4a] space-y-4">
            <div className="flex items-center gap-2.5 text-[#00C853] font-bold text-sm uppercase tracking-wider">
              <CheckCircle2 size={18} />
              <span>The EBON Engineering Solution</span>
            </div>
            <h2 className="text-xl font-bold text-white font-display">Engineered Advantages</h2>
            <div className="space-y-3 pt-2">
              {service.solutions.map((s, i) => (
                <div key={i} className="flex items-start gap-3 text-xs sm:text-sm text-[#D5DFEC] p-3 rounded-lg bg-[#071426] border border-[#1b2f4a]/60">
                  <CheckCircle2 size={16} className="text-[#00C853] mt-0.5 shrink-0" />
                  <span>{s}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* Benefits List */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="p-8 rounded-3xl bg-[#050A13] border border-[#1b2f4a]">
          <h2 className="text-2xl font-bold text-white font-display mb-6">Measurable Commercial Benefits</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {service.benefits.map((benefit, idx) => (
              <div key={idx} className="p-5 rounded-xl bg-[#071426] border border-[#1b2f4a] space-y-2">
                <span className="text-xs font-mono font-bold text-[#159DE8]">0{idx + 1}</span>
                <p className="text-xs sm:text-sm font-semibold text-white leading-relaxed">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Step-by-Step Delivery Process */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        <div>
          <span className="text-xs font-bold uppercase tracking-widest text-[#FF851B] block mb-1">
            METHODOLOGY
          </span>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-display">
            Development & Delivery Lifecycle
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {service.process.map((p, idx) => (
            <div key={idx} className="p-6 rounded-2xl bg-[#0c1a2d] border border-[#1b2f4a] space-y-3">
              <span className="w-8 h-8 rounded-lg bg-[#050A13] border border-[#1b2f4a] flex items-center justify-center text-xs font-mono font-bold text-[#FF851B]">
                {p.step}
              </span>
              <h3 className="text-base font-bold text-white">{p.title}</h3>
              <p className="text-xs text-[#8FA0B8] leading-relaxed">{p.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* If Digital Marketing, show full interactive Keyword Plans and Situation Matcher */}
      {service.slug === 'digital-marketing' && (
        <DigitalMarketingPlans onSelectPlan={(plan) => onOpenConsultation(`Digital Marketing - ${plan}`)} />
      )}

      {/* Service FAQs */}
      {service.faqs && service.faqs.length > 0 && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <h2 className="text-2xl font-bold text-white font-display text-center">Service FAQs</h2>
          <div className="space-y-3">
            {service.faqs.map((faq, i) => (
              <div key={i} className="p-6 rounded-xl bg-[#0c1a2d] border border-[#1b2f4a] space-y-2">
                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                  <HelpCircle size={16} className="text-[#159DE8] shrink-0" />
                  <span>{faq.question}</span>
                </h3>
                <p className="text-xs sm:text-sm text-[#D5DFEC] leading-relaxed pl-6">{faq.answer}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Related Services */}
      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <h2 className="text-xl font-bold text-white font-display">Related Services</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {related.map((r) => (
              <div
                key={r.id}
                onClick={() => onNavigate(`/services/${r.slug}`)}
                className="p-6 rounded-xl bg-[#0c1a2d] border border-[#1b2f4a] hover:border-[#159DE8] transition-all cursor-pointer group"
              >
                <h3 className="text-base font-bold text-white group-hover:text-[#FF851B] transition-colors">{r.title}</h3>
                <p className="text-xs text-[#8FA0B8] mt-1 line-clamp-2">{r.shortDescription}</p>
                <div className="mt-4 flex items-center gap-1 text-xs text-[#159DE8] font-semibold">
                  <span>Learn More</span>
                  <ArrowRight size={12} />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Call to Action Bar */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="p-8 sm:p-12 rounded-3xl bg-gradient-to-r from-[#0B2342] to-[#071426] border border-[#1b2f4a] flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-white font-display">Kickstart Your {service.title} Project</h2>
            <p className="text-sm text-[#D5DFEC]">Speak directly with a lead developer and receive an accurate scope outline.</p>
          </div>
          <button
            onClick={() => onOpenConsultation(service.title)}
            className="px-8 py-3.5 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white text-sm font-bold shadow-lg hover:shadow-glow-orange transition-all shrink-0 flex items-center gap-2"
          >
            <span>Book Consultation</span>
            <ArrowRight size={16} />
          </button>
        </div>
      </section>

    </div>
  );
};
